import { KeyboardArrowRight, Menu as MenuIcon } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  SwipeableDrawer,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import { usePageContext } from "contexts/PageContextProvider";
import { useState } from "react";
import { Link, animateScroll as scroll } from "react-scroll";
// assets
import AstrinLogo from "assets/images/logos/astrin/Astrin_logo__Astrin logo white+green.png";
import { useNavigate } from "react-router-dom";

/**
 * * Header for the website
 * @param {Object} props
 * @param {number} props.scrollTop
 * @returns {React.ReactElement} Header
 */
export default function Header({ scrollTop, type }) {
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const solidHeaderTrigger = scrollTop > 100;
  const solidHeaderTransitionDuration = "400ms";

  return (
    <AppBar
      id="header"
      data-testid="header"
      position="fixed"
      elevation={0}
      sx={{
        pt: 1,
        backgroundColor: solidHeaderTrigger ? "#000c" : "#0000",
        transition: `background-color ${solidHeaderTransitionDuration}`,
      }}
    >
      <Container maxWidth={false}>
        <Toolbar>
          <HeaderLogo />

          {/* Displays on mobile screens */}
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <MobileNavBar
              open={open}
              handleOpen={handleOpen}
              handleClose={handleClose}
              type={type}
            />
          </Box>

          {/* Displays on desktop screens */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
            }}
          >
            <DesktopNavBar handleClose={handleClose} type={type} />
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

/**
 * * Logo for the header
 * @returns {React.ReactElement} HeaderLogo
 */
function HeaderLogo() {
  // TODO: stop using useMediaQuery
  const greaterThanXl = useMediaQuery((theme) => theme.breakpoints.up("xl"));
  const greaterThanMd = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const greaterThanSm = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  return (
    <>
      <Box
        maxWidth={
          greaterThanXl
            ? "25%"
            : greaterThanMd
            ? "30%"
            : greaterThanSm
            ? "50%"
            : "65%"
        }
      >
        <img
          src={AstrinLogo}
          width="100%"
          alt="Astrin Logo"
          onClick={() => scroll.scrollToTop()}
        />
      </Box>
      <Box flexGrow={1} />
    </>
  );
}

function MobileNavBar({ open, handleOpen, handleClose, type }) {
  const { pageSections } = usePageContext();
  return (
    <>
      <IconButton
        aria-label="navigation menu"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleOpen}
        sx={{ color: "white", mb: 1 }}
      >
        <MenuIcon />
      </IconButton>
      <SwipeableDrawer
        anchor="right"
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
      >
        <Box
          sx={{ width: "auto", minWidth: 200 }}
          role="presentation"
          onClick={handleClose}
          onKeyDown={handleClose}
        >
          <List>
            <ListItem sx={{ mt: 2 }}>
              <PressReleaseButton key="press-release" />
            </ListItem>
            {pageSections.map((page, index) => (
              <>
                {type === "press" ? (
                  <PressMobileNavLink
                    key={index}
                    page={page}
                    handleClose={handleClose}
                  />
                ) : (
                  <MainMobileNavLink
                    key={index}
                    page={page}
                    handleClose={handleClose}
                  />
                )}
              </>
            ))}
          </List>
        </Box>
      </SwipeableDrawer>
    </>
  );
}

function PressReleaseButton({ sx }) {
  return (
    <Button
      variant="contained"
      sx={(theme) => ({
        backgroundImage: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main},  ${theme.palette.secondary.light}, ${theme.palette.secondary.main})`,
        backgroundSize: "300% 100%",
        borderRadius: 8,
        fontSize: "110%",
        mb: 1,
        px: 2,
        transition: "all .4s ease-in-out",
        color: "white",
        "&:hover": {
          backgroundPosition: "100% 0",
          transition: "all .4s ease-in-out",
        },
        ...sx,
      })}
      href="https://finance.yahoo.com/news/astrin-biosciences-leading-york-cancer-230400416.html"
    >
      Press Release
    </Button>
  );
}
function DesktopNavBar({ handleClose, type }) {
  const { pageSections } = usePageContext();
  return (
    <>
      <PressReleaseButton key="press-release" />
      {pageSections.map((page, index) => (
        <>
          {type === "press" ? (
            <PressDesktopNavLink
              key={index}
              page={page}
              handleClose={handleClose}
            />
          ) : (
            <MainDesktopNavLink
              key={index}
              page={page}
              handleClose={handleClose}
            />
          )}
        </>
      ))}
    </>
  );
}

// ----- NAV LINKS -----

function MainMobileNavLink({ page, handleClose }) {
  const { scrollOffset, scrollDurationMs } = usePageContext();
  return (
    <Link
      data-testid={`header-mobile-nav-bar-link`}
      to={page.id}
      smooth={true}
      isDynamic={true}
      duration={scrollDurationMs}
      offset={scrollOffset}
    >
      <ListItemButton onClick={handleClose}>
        <KeyboardArrowRight color="primary" />
        <ListItemText primary={page.title} />
      </ListItemButton>
    </Link>
  );
}
function PressMobileNavLink({ page, handleClose }) {
  const navigate = useNavigate();
  const onClick = (id) => {
    handleClose();
    navigate(`/#${id}`);
  };
  return (
    <ListItemButton onClick={() => onClick(page.id)}>
      <KeyboardArrowRight color="primary" />
      <ListItemText primary={page.title} />
    </ListItemButton>
  );
}
function MainDesktopNavLink({ page, handleClose }) {
  const { scrollOffset, scrollDurationMs } = usePageContext();
  // TODO: stop using useMediaQuery
  const greaterThanXl = useMediaQuery((theme) => theme.breakpoints.up("xl"));
  const greaterThanLg = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const greaterThanMd = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const mx = greaterThanXl ? 4 : greaterThanLg ? 3 : greaterThanMd ? 2 : 1;
  return (
    <Button
      onClick={handleClose}
      display="block"
      sx={{ color: "white", fontSize: "110%", mx: mx, mb: 2, mt: 1 }}
    >
      <Link
        data-testid={`header-mobile-nav-bar-link`}
        to={page.id}
        smooth={true}
        isDynamic={true}
        duration={scrollDurationMs}
        offset={scrollOffset}
      >
        {page.title}
      </Link>
    </Button>
  );
}
function PressDesktopNavLink({ page, handleClose }) {
  // TODO: stop using useMediaQuery
  const greaterThanXl = useMediaQuery((theme) => theme.breakpoints.up("xl"));
  const greaterThanLg = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const greaterThanMd = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const mx = greaterThanXl ? 4 : greaterThanLg ? 3 : greaterThanMd ? 2 : 1;
  const navigate = useNavigate();
  const onClick = (id) => {
    handleClose();
    navigate(`/#${id}`);
  };

  return (
    <Button
      onClick={() => onClick(page.id)}
      display="block"
      sx={{ color: "white", fontSize: "110%", mx: mx, mb: 2, mt: 1 }}
    >
      {page.title}
    </Button>
  );
}
